import { useTranslation } from 'react-i18next';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { FieldSeparator } from '@bliks-io/ui';
import {
  FormInput,
  FormTextarea,
  FormUserSelect,
  FormDepartmentSelect
} from '@/components/form/rhf';
import {
  AddDepartmentRequestSchema,
  type AddDepartmentRequest,
  type UpdateDepartmentRequest,
  type Department
} from '@bliks-io/contracts';
import { FormDialog } from '@/components/dialogs/FormDialog';
import { extractParentIdFromPath } from '@/lib/department-utils';
import { extractEntityId } from '@/lib/form-utils';

interface DepartmentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: AddDepartmentRequest | UpdateDepartmentRequest) => unknown;
  department?: Department;
}

/**
 * Form dialog for creating/editing departments
 */
export function DepartmentFormDialog({
  open,
  onOpenChange,
  onSubmit,
  department
}: DepartmentFormDialogProps) {
  const { t } = useTranslation();
  const isEditing = !!department;

  const defaultValues: AddDepartmentRequest = {
    name: department?.name || '',
    description: department?.description || '',
    abbreviation: department?.abbreviation || '',
    manager: extractEntityId(department?.manager),
    parent: extractParentIdFromPath(department?.path)
  };

  return (
    <FormDialog<AddDepartmentRequest>
      open={open}
      onOpenChange={onOpenChange}
      title={
        isEditing
          ? t('organization.departments.editDepartment')
          : t('organization.departments.create')
      }
      description={
        isEditing
          ? t('organization.departments.editDepartmentDescription')
          : t('organization.departments.createDepartmentDescription')
      }
      icon={ENTITY_ICONS.department}
      schema={AddDepartmentRequestSchema}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      isEditing={isEditing}
    >
      {({ form }) => (
        <div className="flex flex-col gap-8">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormInput
              control={form.control}
              name="name"
              label={t('common.Name')}
              placeholder={t('organization.departments.namePlaceholder')}
              required
            />

            <FormInput
              control={form.control}
              name="abbreviation"
              label={t('organizations.Abbreviation')}
              placeholder={t('organization.departments.abbreviation')}
              required
            />
          </div>

          <div className="flex flex-col gap-4">
            <FieldSeparator>{t('organization.departments.relationships')}</FieldSeparator>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormDepartmentSelect
                control={form.control}
                name="parent"
                label={t('organization.departments.parentDepartment')}
                placeholder={t('organization.departments.selectParent')}
                excludeId={department?._id}
                allowClear
                clearLabel={t('departmentSelect.noParentDepartment')}
                onClear={() => form.setValue('parent', '', { shouldDirty: true })}
              />

              <FormUserSelect
                control={form.control}
                name="manager"
                label={t('organization.departments.manager')}
                placeholder={t('organization.departments.selectManager')}
                allowClear
                clearLabel={t('userSelect.noManager')}
                onClear={() => form.setValue('manager', '', { shouldDirty: true })}
              />
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <FieldSeparator>{t('common.Details')}</FieldSeparator>

            <FormTextarea
              control={form.control}
              name="description"
              label={t('common.Description')}
              placeholder={t('organization.departments.descriptionPlaceholder')}
              rows={4}
            />
          </div>
        </div>
      )}
    </FormDialog>
  );
}
